import { supabase } from '@/integrations/supabase/client';
import type { Tables, TablesInsert } from '@/integrations/supabase/types';
import { Article, Sentence, SentenceVerb } from '@/types/article';

// Type aliases for easier use
type DatabaseArticle = Tables<'articles'>;
type DatabaseSentence = Tables<'sentences'>;
type DatabaseSentenceVerb = Tables<'sentence_verbs'>;
type ArticleInsert = TablesInsert<'articles'>;
type SentenceInsert = TablesInsert<'sentences'>;
type SentenceVerbInsert = TablesInsert<'sentence_verbs'>;

// Convert database types to app types
const convertDatabaseArticleToAppArticle = (
  dbArticle: DatabaseArticle,
  dbSentences: DatabaseSentence[],
  dbSentenceVerbs: DatabaseSentenceVerb[] = []
): Article => {
  // Group verbs by sentence_id
  const verbsBySentenceId = dbSentenceVerbs.reduce(
    (acc, verb) => {
      if (!verb.sentence_id) return acc;
      if (!acc[verb.sentence_id]) {
        acc[verb.sentence_id] = [];
      }
      acc[verb.sentence_id].push({
        id: verb.id,
        german: verb.german_word,
        english: verb.english_translation,
        vietnamese: verb.vietnamese_translation,
      });
      return acc;
    },
    {} as Record<string, SentenceVerb[]>
  );

  const sentences: Sentence[] = dbSentences
    .sort((a, b) => a.order_index - b.order_index)
    .map(s => ({
      id: s.id,
      order: s.order_index,
      german: s.german,
      english: s.english,
      vietnamese: s.vietnamese,
      verbs: verbsBySentenceId[s.id] || [],
    }));

  return {
    id: dbArticle.id,
    title: dbArticle.title,
    description: dbArticle.description || undefined,
    sentences,
    createdAt: new Date(dbArticle.created_at || new Date().toISOString()),
    updatedAt: new Date(dbArticle.updated_at || new Date().toISOString()),
    imageUrl: dbArticle.image_url || undefined,
    imagePublicId: dbArticle.image_public_id || undefined,
    originalLink: dbArticle.original_link || undefined,
  };
};

// Fetch all articles with their sentences
export const fetchArticles = async (): Promise<Article[]> => {
  try {
    // Fetch articles
    const { data: articles, error: articlesError } = await supabase
      .from('articles')
      .select('*')
      .order('created_at', { ascending: false });

    if (articlesError) {
      console.error('Error fetching articles:', articlesError);
      return [];
    }

    if (!articles || articles.length === 0) {
      return [];
    }

    // Fetch sentences for all articles
    const articleIds = articles.map(a => a.id);
    const { data: sentences, error: sentencesError } = await supabase
      .from('sentences')
      .select('*')
      .in('article_id', articleIds)
      .order('order_index', { ascending: true });

    if (sentencesError) {
      console.error('Error fetching sentences:', sentencesError);
      // Return articles without sentences rather than failing completely
      return articles.map(article =>
        convertDatabaseArticleToAppArticle(article, [])
      );
    }

    // Fetch sentence verbs for all sentences
    let sentenceVerbs: DatabaseSentenceVerb[] = [];
    if (sentences && sentences.length > 0) {
      const sentenceIds = sentences.map(s => s.id);
      const { data: verbs, error: verbsError } = await supabase
        .from('sentence_verbs')
        .select('*')
        .in('sentence_id', sentenceIds);

      if (verbsError) {
        console.error('Error fetching sentence verbs:', verbsError);
      } else {
        sentenceVerbs = verbs || [];
      }
    }

    // Group sentences by article_id
    const sentencesByArticleId = (sentences || []).reduce(
      (acc, sentence) => {
        if (!sentence.article_id) return acc;
        if (!acc[sentence.article_id]) {
          acc[sentence.article_id] = [];
        }
        acc[sentence.article_id].push(sentence);
        return acc;
      },
      {} as Record<string, DatabaseSentence[]>
    );

    // Convert to app format
    return articles.map(article =>
      convertDatabaseArticleToAppArticle(
        article,
        sentencesByArticleId[article.id] || [],
        sentenceVerbs
      )
    );
  } catch (error) {
    console.error('Unexpected error fetching articles:', error);
    return [];
  }
};

// Save a new article with sentences
export const saveArticle = async (
  userId: string,
  article: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Article> => {
  try {
    if (!userId) {
      throw new Error('User ID is required to save articles');
    }

    // Insert article
    const articleInsert: ArticleInsert = {
      title: article.title,
      description: article.description || null,
      user_id: userId,
      image_url: article.imageUrl || null,
      image_public_id: article.imagePublicId || null,
      original_link: article.originalLink || null,
    };

    const { data: savedArticle, error: articleError } = await supabase
      .from('articles')
      .insert(articleInsert)
      .select()
      .single();

    if (articleError || !savedArticle) {
      console.error('Error saving article:', articleError);
      throw new Error('Failed to save article');
    }

    // Insert sentences
    const sentenceInserts: SentenceInsert[] = article.sentences.map(
      (sentence, index) => ({
        article_id: savedArticle.id,
        order_index: index + 1,
        german: sentence.german,
        english: sentence.english,
        vietnamese: sentence.vietnamese,
      })
    );

    const { data: savedSentences, error: sentencesError } = await supabase
      .from('sentences')
      .insert(sentenceInserts)
      .select();

    if (sentencesError || !savedSentences) {
      console.error('Error saving sentences:', sentencesError);
      // Try to delete the article since sentences failed
      await supabase.from('articles').delete().eq('id', savedArticle.id);
      throw new Error('Failed to save article sentences');
    }

    // Insert sentence verbs if any exist
    const allSentenceVerbs: SentenceVerbInsert[] = [];
    for (let i = 0; i < article.sentences.length; i++) {
      const sentence = article.sentences[i];
      const savedSentence = savedSentences[i];

      if (sentence.verbs && sentence.verbs.length > 0) {
        const verbInserts = sentence.verbs.map(verb => ({
          sentence_id: savedSentence.id,
          german_word: verb.german,
          english_translation: verb.english,
          vietnamese_translation: verb.vietnamese,
        }));
        allSentenceVerbs.push(...verbInserts);
      }
    }

    let savedSentenceVerbs: DatabaseSentenceVerb[] = [];
    if (allSentenceVerbs.length > 0) {
      const { data: verbs, error: verbsError } = await supabase
        .from('sentence_verbs')
        .insert(allSentenceVerbs)
        .select();

      if (verbsError) {
        // Previously this was logged and swallowed, which produced silent
        // empty dictionaries when RLS on sentence_verbs was misconfigured for
        // Clerk auth. Surface the error so callers can show a toast.
        console.error('Error saving sentence verbs:', verbsError);
        throw new Error(
          `Failed to save difficult words: ${verbsError.message}`
        );
      }
      savedSentenceVerbs = verbs || [];

      // PostgREST can return an empty `data` array on RLS-rejected inserts
      // (with no error). Detect that explicitly so we don't claim success
      // when nothing was persisted.
      if (savedSentenceVerbs.length === 0) {
        throw new Error(
          'Failed to save difficult words: insert returned no rows (likely blocked by Row Level Security on sentence_verbs).'
        );
      }
    }

    // Return the complete article
    return convertDatabaseArticleToAppArticle(
      savedArticle,
      savedSentences,
      savedSentenceVerbs
    );
  } catch (error) {
    console.error('Error in saveArticle:', error);
    throw error;
  }
};

// Update an existing article with sentences
export const updateArticle = async (
  userId: string,
  articleId: string,
  article: Omit<Article, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Article> => {
  try {
    if (!userId) {
      throw new Error('User ID is required to update articles');
    }

    // Update article
    const { data: updatedArticle, error: articleError } = await supabase
      .from('articles')
      .update({
        title: article.title,
        description: article.description || null,
        image_url: article.imageUrl || null,
        image_public_id: article.imagePublicId || null,
        original_link: article.originalLink || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', articleId)
      .eq('user_id', userId) // Ensure user can only update their own articles
      .select()
      .single();

    if (articleError || !updatedArticle) {
      console.error('Error updating article:', articleError);
      throw new Error('Failed to update article');
    }

    // Delete existing sentences and verbs (CASCADE will handle verbs)
    const { error: deleteSentencesError } = await supabase
      .from('sentences')
      .delete()
      .eq('article_id', articleId);

    if (deleteSentencesError) {
      console.error('Error deleting existing sentences:', deleteSentencesError);
      throw new Error('Failed to update article sentences');
    }

    // Insert new sentences
    const sentenceInserts: SentenceInsert[] = article.sentences.map(
      (sentence, index) => ({
        article_id: articleId,
        order_index: index + 1,
        german: sentence.german,
        english: sentence.english,
        vietnamese: sentence.vietnamese,
      })
    );

    const { data: savedSentences, error: sentencesError } = await supabase
      .from('sentences')
      .insert(sentenceInserts)
      .select();

    if (sentencesError || !savedSentences) {
      console.error('Error saving new sentences:', sentencesError);
      throw new Error('Failed to update article sentences');
    }

    // Insert sentence verbs if any exist
    const allSentenceVerbs: SentenceVerbInsert[] = [];
    for (let i = 0; i < article.sentences.length; i++) {
      const sentence = article.sentences[i];
      const savedSentence = savedSentences[i];

      if (sentence.verbs && sentence.verbs.length > 0) {
        const verbInserts = sentence.verbs.map(verb => ({
          sentence_id: savedSentence.id,
          german_word: verb.german,
          english_translation: verb.english,
          vietnamese_translation: verb.vietnamese,
        }));
        allSentenceVerbs.push(...verbInserts);
      }
    }

    let savedSentenceVerbs: DatabaseSentenceVerb[] = [];
    if (allSentenceVerbs.length > 0) {
      const { data: verbs, error: verbsError } = await supabase
        .from('sentence_verbs')
        .insert(allSentenceVerbs)
        .select();

      if (verbsError) {
        console.error('Error saving sentence verbs:', verbsError);
        throw new Error(
          `Failed to save difficult words: ${verbsError.message}`
        );
      }
      savedSentenceVerbs = verbs || [];

      if (savedSentenceVerbs.length === 0) {
        throw new Error(
          'Failed to save difficult words: insert returned no rows (likely blocked by Row Level Security on sentence_verbs).'
        );
      }
    }

    // Return the complete updated article
    return convertDatabaseArticleToAppArticle(
      updatedArticle,
      savedSentences,
      savedSentenceVerbs
    );
  } catch (error) {
    console.error('Error in updateArticle:', error);
    throw error;
  }
};

// Delete an article (and its sentences due to CASCADE)
export const deleteArticle = async (articleId: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('articles')
      .delete()
      .eq('id', articleId);

    if (error) {
      console.error('Error deleting article:', error);
      throw new Error('Failed to delete article');
    }
  } catch (error) {
    console.error('Error in deleteArticle:', error);
    throw error;
  }
};
